import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from './ui/Input';
import Button from './ui/Button';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/apiClient';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'register'

  // null пока флаг не загружен — так ссылка на регистрацию не мелькает
  // до ответа сервера. Значение приходит из ENABLE_REGISTRATION на бэкенде.
  const [registrationEnabled, setRegistrationEnabled] = useState(null);

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Register state
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [regError, setRegError] = useState('');
  const [regSuccess, setRegSuccess] = useState(false);
  const [regLoading, setRegLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get('/auth/config')
      .then((res) => {
        if (!cancelled) setRegistrationEnabled(res.data?.data?.registrationEnabled === true);
      })
      .catch(() => {
        // Не удалось спросить сервер — не предлагаем форму, которую он всё
        // равно отклонит.
        if (!cancelled) setRegistrationEnabled(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      await login({ email: loginEmail, password: loginPassword });
      navigate('/', { replace: true });
    } catch (err) {
      setLoginError(err.response?.data?.message || 'Ошибка входа');
    } finally {
      setLoginLoading(false);
    }
  };

  const validateRegPassword = (pwd) => {
    if (pwd.length < 8) return 'Пароль должен содержать не менее 8 символов';
    if (!/[A-Z]/.test(pwd)) return 'Пароль должен содержать заглавную букву';
    if (!/[a-z]/.test(pwd)) return 'Пароль должен содержать строчную букву';
    if (!/[0-9]/.test(pwd)) return 'Пароль должен содержать цифру';
    return null;
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setRegError('');

    const pwdError = validateRegPassword(regPassword);
    if (pwdError) { setRegError(pwdError); return; }
    if (regPassword !== regConfirm) { setRegError('Пароли не совпадают'); return; }

    setRegLoading(true);
    try {
      await apiClient.post('/auth/register', { email: regEmail, password: regPassword });
      setRegSuccess(true);
    } catch (err) {
      if (err.response?.status === 403) {
        // Флаг выключили уже после загрузки страницы — убираем форму.
        setRegistrationEnabled(false);
        setMode('login');
        setLoginError('Регистрация отключена администратором');
        return;
      }
      setRegError(err.response?.data?.message || 'Ошибка регистрации');
    } finally {
      setRegLoading(false);
    }
  };

  const switchToLogin = () => {
    setMode('login');
    setRegEmail('');
    setRegPassword('');
    setRegConfirm('');
    setRegError('');
    setRegSuccess(false);
  };

  const switchToRegister = () => {
    setMode('register');
    setLoginEmail('');
    setLoginPassword('');
    setLoginError('');
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-base px-6">
      <div className="w-full max-w-lg rounded-xl3 border border-glass-border bg-glass px-8 py-10 backdrop-blur-lg shadow-glass">

        {!(mode === 'register' && registrationEnabled) ? (
          <>
            <div className="space-y-3 text-center">
              <h1 className="text-3xl font-semibold text-ink">BudgetApp</h1>
              <p className="text-base text-muted">Введите логин и пароль для доступа</p>
            </div>
            {loginError && (
              <div className="mt-6 rounded-2xl border border-line bg-base p-4 text-sm" style={{ color: '#dc2626' }}>
                {loginError}
              </div>
            )}
            <form onSubmit={handleLogin} className="mt-6 space-y-6">
              <Input
                label="Логин (email)"
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                required
              />
              <Input
                label="Пароль"
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                required
              />
              <Button type="submit" loading={loginLoading} className="w-full justify-center">
                Войти
              </Button>
            </form>
            {registrationEnabled && (
              <p className="mt-6 text-center text-sm text-muted">
                Нет аккаунта?{' '}
                <button
                  type="button"
                  onClick={switchToRegister}
                  style={{ color: 'var(--accent-0)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  Зарегистрироваться
                </button>
              </p>
            )}
          </>
        ) : (
          <>
            <div className="space-y-3 text-center">
              <h1 className="text-3xl font-semibold text-ink">Регистрация</h1>
              <p className="text-base text-muted">Создайте новую учётную запись</p>
            </div>

            {regSuccess ? (
              <div className="mt-8 space-y-6 text-center">
                <div style={{ fontSize: 48 }}>✓</div>
                <p className="text-ink font-semibold">Аккаунт создан!</p>
                <p className="text-sm text-muted">Теперь вы можете войти с указанными данными.</p>
                <Button type="button" onClick={switchToLogin} className="w-full justify-center">
                  Войти
                </Button>
              </div>
            ) : (
              <>
                {regError && (
                  <div className="mt-6 rounded-2xl border border-line bg-base p-4 text-sm" style={{ color: '#dc2626' }}>
                    {regError}
                  </div>
                )}
                <form onSubmit={handleRegister} className="mt-6 space-y-5">
                  <Input
                    label="Email"
                    type="email"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    required
                  />
                  <Input
                    label="Пароль"
                    type="password"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    required
                    placeholder="Мин. 8 символов, A-Z, a-z, 0-9"
                  />
                  <Input
                    label="Подтвердите пароль"
                    type="password"
                    value={regConfirm}
                    onChange={(e) => setRegConfirm(e.target.value)}
                    required
                  />
                  <Button type="submit" loading={regLoading} className="w-full justify-center">
                    Создать аккаунт
                  </Button>
                </form>
                <p className="mt-6 text-center text-sm text-muted">
                  Уже есть аккаунт?{' '}
                  <button
                    type="button"
                    onClick={switchToLogin}
                    style={{ color: 'var(--accent-0)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    Войти
                  </button>
                </p>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
